import { setHierarchyOutput, setRowGroupColumnIds } from '@/tableDefinitionEditor/assetBuilder.actions';
import { applySavedFilterToGrid } from '@/tableDefinitionEditor/filterSpec.restore.utilities';
import { type WithFilterSpec, readFilterSpec } from '@/tableDefinitionEditor/filterSpec.types';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import { setTableDefinition } from '@/tableDefinitionEditor/tableDefinition.actions';
import { CONTROL_PANE_DISPLAY } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  addOrUpdateColumnDefinitions,
  columnDefinitionOutputToColumnDefinitionInput,
  updateSortsInTheGrid,
  updateVisibilityInTheGrid,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { setControlPane, setEnableTreeCreation } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { doTrack } from '@/track/trackProxy.service';
import { sqHierarchiesApi } from '@seeqdev/api/endpoint/HierarchiesApi';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnState } from '@seeqdev/ag/grid';
import type { HierarchyInputV1 } from '@seeqdev/api/model/HierarchyInputV1';
import type { HierarchyOutputV1 } from '@seeqdev/api/model/HierarchyOutputV1';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';
import type { ViewColumnInputV1 } from '@seeqdev/api/model/ViewColumnInputV1';

/**
 * Type-safe helper to update specific properties of an object.
 * Uses Pick to preserve exact property types from T (not optional versions).
 * TypeScript infers K from the keys present in the changes object.
 *
 * @template T - The object type being updated
 * @template K - The keys being updated (inferred from changes)
 * @param obj - The original object
 * @param changes - Updates to apply (must include all specified keys with exact types from T)
 * @returns A new object with changes applied, typed as T
 */
function updateObject<T, K extends keyof T>(obj: T, changes: Pick<T, K>): T {
  return { ...obj, ...changes };
}

export const hierarchyOutputToHierarchyInput = (hierarchyOutput: HierarchyOutputV1): HierarchyInputV1 => {
  return {
    name: hierarchyOutput.name,
    description: hierarchyOutput.description,
    tableDefinitionId: hierarchyOutput.tableDefinition.id,
    aliasColumnId: hierarchyOutput.aliasColumnId,
    ...((hierarchyOutput as WithFilterSpec).filterSpec
      ? { filterSpec: (hierarchyOutput as WithFilterSpec).filterSpec! }
      : {}),
    viewColumns: hierarchyOutput.viewColumns.map((col) => ({ ...col }) as Required<ViewColumnInputV1>),
  };
};

/**
 * Applies hierarchy view columns to column definitions, returning modified columns and grouping state.
 * Columns in the hierarchy get their sort/visibility from viewColumns; others are cleared.
 */
const applyHierarchyToColumns = (
  columns: ColumnDefinition[],
  viewColumns: HierarchyOutputV1['viewColumns'],
): { modifiedColumns: ColumnDefinition[]; columnStates: ColumnState[] } => {
  // Build a map for O(1) lookup of viewColumns by columnDefinition ID
  const viewColumnMap = new Map(viewColumns.map((vc) => [vc.columnDefinition, vc]));

  const columnsToShow: ColumnDefinition[] = [];
  const columnsToHide: ColumnDefinition[] = [];

  // Single pass through columns to categorize and apply metadata
  for (const col of columns) {
    const viewColumn = viewColumnMap.get(col.id);
    if (viewColumn) {
      columnsToShow.push(
        updateObject(col, {
          sortAscending: viewColumn.sortAscending ?? false,
          sortIndex: viewColumn.sortIndex,
          isHidden: viewColumn.isHidden ?? false,
        }),
      );
    } else {
      columnsToHide.push(
        updateObject(col, {
          sortAscending: false,
          sortIndex: undefined,
          isHidden: false,
        }),
      );
    }
  }

  const modifiedColumns = [...columnsToShow, ...columnsToHide];

  // Build grouping state from viewColumns with groupOrder
  const columnStates: ColumnState[] = columnsToShow
    .map((col) => {
      const viewColumn = viewColumnMap.get(col.id);
      if (viewColumn?.groupOrder !== undefined) {
        return {
          colId: col.id,
          rowGroupIndex: viewColumn.groupOrder,
        } as ColumnState;
      }
      return null;
    })
    .filter((state): state is ColumnState => state !== null);

  return { modifiedColumns, columnStates };
};

/**
 * Apply hierarchy view column metadata to the grid visually without saving to backend.
 * Used in read-only mode to show the hierarchy structure without requiring WRITE permission.
 */
export const applyViewColumnMetadataToGridOnly = async (hierarchy: HierarchyOutputV1) => {
  const sqItemTableDefinitionStore = getTableDefinitionStore('ITEM');
  const agGridApi = await sqItemTableDefinitionStore.waitForAgGridApi();

  if (!agGridApi) {
    console.warn('[applyViewColumnMetadataToGridOnly] Grid API not available');
    return;
  }

  const { modifiedColumns, columnStates } = applyHierarchyToColumns(
    sqItemTableDefinitionStore.columns,
    hierarchy.viewColumns,
  );

  // Create an in-memory modified table definition (don't save to backend)
  // Type assertion is safe: updateVisibilityInTheGrid and updateSortsInTheGrid only access columnDefinitions
  const modifiedTableDefinition = {
    columnDefinitions: modifiedColumns,
  } as Pick<TableDefinitionOutputV1, 'columnDefinitions'> as TableDefinitionOutputV1;

  // Apply visibility, sorts, grouping, and the hierarchy's saved filter to the grid
  updateVisibilityInTheGrid(modifiedTableDefinition, agGridApi);
  updateSortsInTheGrid(modifiedTableDefinition, agGridApi);
  agGridApi.applyColumnState({ state: columnStates });
  applySavedFilterToGrid(
    agGridApi,
    readFilterSpec(hierarchy),
    sqItemTableDefinitionStore.columns,
    sqItemTableDefinitionStore.tableJoinData,
  );
};

export const applyViewColumnMetadataToTableDefinition = async (hierarchy: HierarchyOutputV1) => {
  const sqItemTableDefinitionStore = getTableDefinitionStore('ITEM');
  const columns = sqItemTableDefinitionStore.columns;
  const agGridApi = await sqItemTableDefinitionStore.waitForAgGridApi();

  const { modifiedColumns, columnStates } = applyHierarchyToColumns(columns, hierarchy.viewColumns);

  const allColumnAsInputs = modifiedColumns.map((col) => {
    return columnDefinitionOutputToColumnDefinitionInput(col, columns);
  });

  const results = await addOrUpdateColumnDefinitions(allColumnAsInputs, sqItemTableDefinitionStore.id, true);
  if (!results) {
    console.error('Failed to update column definitions');
    return;
  }
  setTableDefinition('ITEM', results.tableDefinition);

  // Apply visibility, sorts, grouping, and the hierarchy's saved filter to the grid
  if (agGridApi) {
    updateVisibilityInTheGrid(results.tableDefinition, agGridApi);
    updateSortsInTheGrid(results.tableDefinition, agGridApi);
    agGridApi.applyColumnState({ state: columnStates });
    applySavedFilterToGrid(agGridApi, readFilterSpec(hierarchy), columns, sqItemTableDefinitionStore.tableJoinData);
  }
};

export const loadHierarchyAndEdit = async (hierarchyId: string): Promise<void> => {
  // fetch the hierarchy fresh to ensure we have the latest version
  const { data: hierarchy } = await sqHierarchiesApi.getHierarchy({ id: hierarchyId });
  const itemTableId = hierarchy.tableDefinition.id;
  doTrack('Tree Builder', 'Edit tree button clicked', {
    itemTableId,
    treeId: hierarchyId,
  });
  setControlPane('ITEM', CONTROL_PANE_DISPLAY.HIERARCHY_CONTROL);
  setHierarchyOutput(hierarchy);

  // Extract rowGroupColumnIds from viewColumns that have groupOrder defined
  const rowGroupColumnIds = hierarchy.viewColumns
    .filter((vc) => vc.groupOrder !== undefined)
    .sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0))
    .map((vc) => vc.columnDefinition);
  setRowGroupColumnIds(rowGroupColumnIds);

  setEnableTreeCreation('ITEM', true);
  await applyViewColumnMetadataToTableDefinition(hierarchy);
};

/**
 * Load a hierarchy for viewing in read-only mode.
 * Shows the ConfigureHierarchy panel in read-only mode and applies the hierarchy visually to the grid.
 */
export const loadHierarchyAndView = async (hierarchyId: string): Promise<void> => {
  // fetch the hierarchy fresh to ensure we have the latest version
  const { data: hierarchy } = await sqHierarchiesApi.getHierarchy({ id: hierarchyId });
  const itemTableId = hierarchy.tableDefinition.id;
  doTrack('Tree Builder', 'View tree button clicked', {
    itemTableId,
    hierarchyId,
  });
  setControlPane('ITEM', CONTROL_PANE_DISPLAY.HIERARCHY_CONTROL);
  setHierarchyOutput(hierarchy);

  // Extract rowGroupColumnIds from viewColumns that have groupOrder defined
  const rowGroupColumnIds = hierarchy.viewColumns
    .filter((vc) => vc.groupOrder !== undefined)
    .sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0))
    .map((vc) => vc.columnDefinition);
  setRowGroupColumnIds(rowGroupColumnIds);

  setEnableTreeCreation('ITEM', true);
  await applyViewColumnMetadataToGridOnly(hierarchy);
  // Note: The ConfigureHierarchy component uses TableEditorModeContext to disable edit controls in read-only mode
  // Note: DO NOT call applyViewColumnMetadataToTableDefinition() - it requires WRITE permission
  // Instead, we use applyViewColumnMetadataToGridOnly() to apply changes visually without backend save
};

export const filterHierarchiesUsingThisColumnAsGroup = (columnId: string, hierarchies: HierarchyOutputV1[]) => {
  return hierarchies.filter((hierarchy) => {
    return hierarchy.viewColumns.some((viewColumn) => {
      return viewColumn.groupOrder !== undefined && viewColumn.columnDefinition === columnId;
    });
  });
};
